import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { B2bContractDriver } from '../../../models/Contract';
import { ContractsService } from '../../../services/contracts.service';
import { ErrorService } from '../../../shared/services/error.service';
import { ConfirmationService } from '../../../shared/services/confirmation.service';
import { TableAction } from '../../../shared/components/table/table.component';
import { B2B_EXPORT_PAGE_SIZE, exportB2bTableToExcel, rowsFromPagedResponse } from '../../../utils/b2b-table-export.util';

@Component({
  selector: 'app-b2b-contracts-drivers',
  templateUrl: './b2b-contracts-drivers.component.html',
  styleUrls: ['./b2b-contracts-drivers.component.css']
})
export class B2bContractsDriversComponent implements OnInit {
  drivers: B2bContractDriver[] = [];
  isLoading = false;
  contractId: number | null = null;
  contractNumber: string = '';
  contractName: string = '';

  // Paginação (backend)
  pageNumber = 1;
  pageSize = 10;
  totalItems = 0;
  totalPages = 0;
  searchTerm = '';


  // Configuração da tabela genérica
  columns = [
    { titulo: 'Nome', label: 'name' },
    { titulo: 'CPF', label: 'driverCpf', formatter: (value: any) => this.formatCpfCnpj(value) },
    { titulo: 'CNH', label: 'driverCnh' },
    { titulo: 'Matrícula', label: 'employeeRegistration' },
    { titulo: 'Telefone', label: 'phone', formatter: (value: any) => this.formatPhone(value) },
    { titulo: 'Endereço', label: 'address' },
  ];

  actions: TableAction[] = [
    {
      label: 'Visualizar',
      icon: 'fas fa-eye',
      action: 'view',
      class: 'btn-view'
    },
    {
      label: 'Editar',
      icon: 'fas fa-edit',
      action: 'edit',
      class: 'btn-edit'
    },
    {
      label: 'Excluir',
      icon: 'fas fa-trash',
      action: 'delete',
      class: 'btn-delete'
    }
  ];

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private contractsService: ContractsService,
    private errorService: ErrorService,
    private confirmationService: ConfirmationService
  ) {}

  ngOnInit(): void {
    this.route.params.subscribe(params => {
      this.contractId = +params['id'];
      if (this.contractId) {
        this.loadDrivers();
        this.loadContractInfo();
      }
    });
  }

  loadContractInfo(): void {
    if (!this.contractId) return;

    this.contractsService.getB2bContractById(this.contractId).subscribe({
      next: (contract) => {
        this.contractNumber = contract.contractNumber || '';
        this.contractName = contract.contractName || '';
      },
      error: (err) => {
        this.errorService.handleHttpError(err);
      }
    });
  }

  loadDrivers(): void {
    if (!this.contractId) return;

    this.isLoading = true;

    this.contractsService.getB2bContractDrivers(this.contractId, this.pageNumber, this.pageSize, this.searchTerm).subscribe({
      next: (response) => {
        // A resposta agora vem com estrutura paginada
        let driversData: any[] = [];
        
        if (response.data && Array.isArray(response.data)) {
          driversData = response.data;
        } else if (response.Data && Array.isArray(response.Data)) {
          driversData = response.Data;
        } else if (Array.isArray(response)) {
          driversData = response;
        }

        this.drivers = driversData.map((driver: any) => this.toDriverRow(driver));

        // Atualizar informações de paginação
        const pag = response.pagination || response.Pagination || {};
        this.totalItems = pag.totalItems || pag.TotalItems || this.drivers.length;
        this.totalPages = pag.totalPages || pag.TotalPages || Math.max(1, Math.ceil(this.totalItems / Math.max(1, this.pageSize)));
        this.pageNumber = pag.currentPage || pag.CurrentPage || this.pageNumber;
        
        this.isLoading = false;
      },
      error: (err) => {
        this.errorService.handleHttpError(err);
        this.isLoading = false;
      }
    });
  }

  onSearchChange(term: string): void {
    this.searchTerm = (term || '').trim();
    this.pageNumber = 1;
    this.loadDrivers();
  }

  onPageChange(page: number): void {
    if (!page || page === this.pageNumber) return;
    this.pageNumber = page;
    this.loadDrivers();
  }

  onPageSizeChange(size: number): void {
    this.pageSize = size;
    this.pageNumber = 1;
    this.loadDrivers();
  }

  onActionClick(actionData: { action: string; item: B2bContractDriver }): void {
    const { action, item } = actionData;

    switch (action) {
      case 'view':
        if (item.id != null) this.viewDriver(item.id);
        break;
      case 'edit':
        if (item.id != null) this.editDriver(item.id);
        break;
      case 'delete':
        if (item.id != null) this.deleteDriver(item.id, item.name);
        break;
      default:
        // eslint-disable-next-line no-console
    }
  }

  viewDriver(driverId: number): void {
    if (!this.contractId) return;
    this.router.navigate(['/b2b/contracts', this.contractId, 'drivers', driverId, 'view']);
  }

  editDriver(driverId: number): void {
    if (!this.contractId) return;
    this.router.navigate(['/b2b/contracts', this.contractId, 'drivers', driverId, 'edit']);
  }

  newDriver(): void {
    if (!this.contractId) return;
    this.router.navigate(['/b2b/contracts', this.contractId, 'drivers', 'new']);
  }

  deleteDriver(driverId: number, driverName: string): void {
    if (!this.contractId) return;

    this.confirmationService.confirmDelete(driverName || 'este condutor', 'o condutor').subscribe({
      next: (confirmed: boolean) => {
        if (confirmed) {
          this.contractsService.deleteB2bContractDriver(this.contractId!, driverId).subscribe({
            next: () => {
              this.errorService.showSuccessMessage('Condutor excluído com sucesso!');
              this.loadDrivers();
            },
            error: (err) => {
              this.errorService.handleHttpError(err);
            }
          });
        }
      }
    });
  }

  exportExcel(): void {
    if (!this.contractId) return;
    const size = Math.max(this.totalItems || 0, B2B_EXPORT_PAGE_SIZE);
    this.contractsService.getB2bContractDrivers(this.contractId, 1, size, this.searchTerm).subscribe({
      next: (response) => {
        const rows = rowsFromPagedResponse(response).map((driver: any) => this.toDriverRow(driver));
        exportB2bTableToExcel(
          `b2b-condutores-${this.contractNumber || this.contractId}`,
          this.columns,
          rows,
          'Condutores'
        );
      },
      error: (err) => this.errorService.handleHttpError(err)
    });
  }

  navigateBack(): void {
    if (this.contractId) {
      this.router.navigate(['/b2b/contracts', this.contractId, 'view']);
    } else {
      this.router.navigate(['/b2b/contracts']);
    }
  }

  private toDriverRow(driver: any): B2bContractDriver {
    return {
      id: driver.Id || driver.id,
      name: driver.Name || driver.name || '',
      driverCpf: driver.DriverCpf || driver.driverCpf || '',
      driverCnh: driver.DriverCnh || driver.driverCnh || '',
      phone: driver.Phone || driver.phone || '',
      email: driver.Email || driver.email || '',
      address: driver.Address || driver.address || '',
      cnhCategory: driver.CnhCategory || driver.cnhCategory || null,
      cnhExpirationDate: driver.CnhExpirationDate || driver.cnhExpirationDate || null,
      employeeRegistration: driver.EmployeeRegistration || driver.employeeRegistration || null,
      admissionDate: driver.AdmissionDate || driver.admissionDate || null,
      birthDate: driver.BirthDate || driver.birthDate || null,
      coordinatorName: driver.CoordinatorName || driver.coordinatorName || null,
      leaderName: driver.LeaderName || driver.leaderName || null,
      addressStreet: driver.AddressStreet || driver.addressStreet || null,
      addressNumber: driver.AddressNumber || driver.addressNumber || null,
      addressNeighborhood: driver.AddressNeighborhood || driver.addressNeighborhood || null,
      addressZipCode: driver.AddressZipCode || driver.addressZipCode || null,
      addressCity: driver.AddressCity || driver.addressCity || null,
      addressState: driver.AddressState || driver.addressState || null,
      contractNumber: driver.ContractNumber || driver.contractNumber || 'N/A',
      b2bContractId: driver.B2BContractId || driver.b2bContractId,
      termoResponsabilidadeUrl: driver.TermoResponsabilidadeUrl || driver.termoResponsabilidadeUrl,
      cnhDocumentUrl: driver.CnhDocumentUrl || driver.cnhDocumentUrl,
      createdAt: driver.CreatedAt || driver.createdAt,
      updatedAt: driver.UpdatedAt || driver.updatedAt
    };
  }

  formatDate(dateString: string): string {
    if (!dateString) return 'N/A';
    try {
      return new Date(dateString).toLocaleDateString('pt-BR');
    } catch {
      return dateString;
    }
  }

  formatCpfCnpj(value: string): string {
    if (!value) return '-';
    const digits = String(value).replace(/\D/g, '');
    
    if (digits.length === 11) {
      // CPF
      return digits.replace(/^(\d{3})(\d{3})(\d{3})(\d{2})$/, '$1.$2.$3-$4');
    } else if (digits.length === 14) {
      // CNPJ
      return digits.replace(/^(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})$/, '$1.$2.$3/$4-$5');
    }
    return value;
  }

  formatPhone(value: string): string {
    if (!value) return '-';
    const digits = String(value).replace(/\D/g, '');
    
    if (digits.length === 10) {
      return digits.replace(/^(\d{2})(\d{4})(\d{4})$/, '($1) $2-$3');
    } else if (digits.length === 11) {
      return digits.replace(/^(\d{2})(\d{5})(\d{4})$/, '($1) $2-$3');
    }
    return value;
  }
}
